import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BaseHttpService } from '../http/base.http.service';
import { ApiRouterService } from '../http/api.router';
import { objectToSnake } from 'ts-case-convert';
import { SuggestionSortType } from 'src/app/models/enums/suggestionSortType';
import { IBase } from 'src/app/models/base';
import { ISuggestion } from 'src/app/models/suggestion';
import { CommunicationUserType, SuggestionVisibility } from 'src/app/models/enums';
import { ISuggestionsFilters } from 'src/app/filters/suggestionsFilters';
import {
  buildQueryString,
  serializeIdList,
  serializeNumberList,
  serializeStringList,
} from 'src/app/filters/filterUtils';

function entityReference(entity?: IBase | null): IBase | null {
  return entity?.id ? { id: entity.id } : null;
}

@Injectable({
  providedIn: 'root',
})
export class SuggestionHttpService extends BaseHttpService {
  private baseURL = `suggestions`;

  constructor(
    @Inject(HttpClient) httpClient: HttpClient,
    private routerService: ApiRouterService,
  ) {
    super(httpClient);
  }

  public createSuggestion(suggestion: ISuggestion) {
    const url = this.routerService.mount(`${this.baseURL}`);
    return this.create(this.buildSuggestionBody(suggestion), url);
  }

  public updateSuggestion(suggestion: ISuggestion) {
    const url = this.routerService.mount(`${this.baseURL}/${suggestion.id}`);
    return this.update(this.buildSuggestionBody(suggestion), url);
  }

  private buildSuggestionBody(suggestion: ISuggestion) {
    return objectToSnake({
      type: suggestion.type,
      affair: suggestion.affair,
      description: suggestion.description,
      comments: suggestion.comments,
      priority: suggestion.priority,
      status: suggestion.status,
      town: entityReference(suggestion.town),
      client: entityReference(suggestion.client),
      service: entityReference(suggestion.service),
      userSuggester: entityReference(suggestion.userSuggester),
      expedition: entityReference(suggestion.expedition),
      visibility: suggestion.visibility ?? SuggestionVisibility.PRIVATE,
      publicMessage: suggestion.publicMessage || null,
    });
  }

  public getSuggestions(
    userType: CommunicationUserType,
    page: number = 1,
    size: number = 10,
    sort: SuggestionSortType = SuggestionSortType.CREATION,
  ) {
    let path = `?page=${page}&size=${size}&sorting=${sort}`;
    const url = this.routerService.mount(`${this.baseURL}/${userType}${path}`);
    return this.read(url);
  }

  public getSuggestionById(id: number) {
    const url = this.routerService.mount(`${this.baseURL}/${id}`);
    return this.read(url);
  }

  public filter(
    communicationUserType: CommunicationUserType,
    page: number = 1,
    size: number = 10,
    sort: SuggestionSortType = SuggestionSortType.CREATION,
    filters: ISuggestionsFilters,
    searchValue?: string,
  ) {
    const queryParams: Record<string, string | null> = {
      clients: serializeNumberList(filters.selectedClients),
      towns: serializeNumberList(filters.selectedTowns),
      services: serializeIdList(filters.selectedServices),
      priorities: serializeStringList(filters.selectedPriorities),
      types: serializeStringList(filters.selectedTypes),
      status: serializeStringList(filters.selectedStatus),
      userResolutions: serializeStringList(filters.selectedUserResolutions),
      visibilities: serializeStringList(filters.selectedVisibilities),
      expeditions: serializeIdList(filters.selectedExpeditions),
      search: searchValue || null,
    };
    const path = `/filter?page=${page}&size=${size}&sorting=${sort}${buildQueryString(queryParams).replace('?', '&')}`;
    const url = this.routerService.mount(`${this.baseURL}/${communicationUserType}${path}`);
    return this.read(url);
  }

  public getActiveTickets(expeditionIds: number[]) {
    const url = this.routerService.mount(`${this.baseURL}/active?expeditions=${expeditionIds.join(',')}`);
    return this.read(url);
  }

  public sendConversationMessage(suggestionId: number, newMessage: string) {
    const url = this.routerService.mount(`${this.baseURL}/${suggestionId}/comments`);
    return this.create(newMessage, url);
  }

  public getPendingSuggestionsCount() {
    const url = this.routerService.mount(`${this.baseURL}/pending`);
    return this.read(url);
  }
}
